/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";

interface CommandPaletteContextValue {
  isOpen: boolean;
  /** Initial query to populate when opening */
  initialQuery: string;
  open: () => void;
  /** Open with a pre-filled query */
  openWithQuery: (query: string) => void;
  close: () => void;
  toggle: () => void;
  /** Clear the initial query (call after consuming it) */
  clearInitialQuery: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(
  null,
);

// Short window to preserve query when reopening (3 seconds)
const QUERY_PRESERVE_WINDOW_MS = 3000;

export function CommandPaletteProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");
  const lastClosedRef = useRef<number>(0);
  const lastQueryRef = useRef<string>("");

  const open = useCallback(() => {
    // If reopening within the preserve window, restore the last query
    const now = Date.now();
    if (
      now - lastClosedRef.current < QUERY_PRESERVE_WINDOW_MS &&
      lastQueryRef.current
    ) {
      setInitialQuery(lastQueryRef.current);
    }
    setIsOpen(true);
  }, []);

  const openWithQuery = useCallback((query: string) => {
    setInitialQuery(query);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    lastClosedRef.current = Date.now();
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        // Closing
        lastClosedRef.current = Date.now();
      } else {
        // Opening - check for query preservation
        const now = Date.now();
        if (
          now - lastClosedRef.current < QUERY_PRESERVE_WINDOW_MS &&
          lastQueryRef.current
        ) {
          setInitialQuery(lastQueryRef.current);
        }
      }
      return !prev;
    });
  }, []);

  const clearInitialQuery = useCallback(() => {
    // Store the current query before clearing for potential restoration
    if (initialQuery) {
      lastQueryRef.current = initialQuery;
    }
    setInitialQuery("");
  }, [initialQuery]);

  return (
    <CommandPaletteContext.Provider
      value={{
        isOpen,
        initialQuery,
        open,
        openWithQuery,
        close,
        toggle,
        clearInitialQuery,
      }}
    >
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette() {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error(
      "useCommandPalette must be used within a CommandPaletteProvider",
    );
  }
  return context;
}
